import { type ReactElement, useCallback } from 'react';
import cn from 'classnames';
import {
    HStack,
    IconCashback,
    IconDocument,
    IconGift,
    IconJob,
    IconUser,
    Text,
    useStack,
    VStack,
} from '@direct-frontend/components';
import { type Page } from '#shared/api/schema';
import { useChangeConstructorRightBarSection } from '../../hooks/useChangeConstructorRightBarSection';
import { useSortConstructorPages } from '../../hooks/useSortConstructorPages';
import { changeConstructorPage } from '../../model/slices';
import { getConstructorPageIcon } from '../../model/utils/getConstructorPageIcon';
import {
    getConstructorCartPageItemTitle,
    isConstructorCartPageGroup,
} from '../../model/utils/groupConstructorPagesByAlias';
import styles from './ConstructorPagesMiniGrid.module.css';

interface ConstructorPagesMiniGridProps {
    pages: Page[];
    onClick: VoidFunction;
}

const PageItem = (props: { page: Page; onClose: VoidFunction }) => {
    const { page, onClose } = props;

    const changeConstructorRightBarSection = useChangeConstructorRightBarSection();

    const onClick = useCallback(() => {
        changeConstructorPage(page.id);
        changeConstructorRightBarSection();
        onClose();
    }, [page, changeConstructorRightBarSection, onClose]);

    return (
        <div onClick={onClick}>
            <HStack gap="8" className={styles.pageItem} alignItems="center">
                <HStack className={styles.icon} alignItems="center">
                    {getConstructorPageIcon(page.alias)}
                </HStack>
                <Text role="main" size="xs" title={page.title}>
                    {page.title}
                </Text>
            </HStack>
        </div>
    );
};

const PageItemAccordion = (props: { pages: Page[]; icon: ReactElement; title: string; onClose: VoidFunction }) => {
    const { pages, icon, title, onClose } = props;

    const changeConstructorRightBarSection = useChangeConstructorRightBarSection();

    const onClick = useCallback(() => {
        changeConstructorPage(pages[0].id);
        changeConstructorRightBarSection();
    }, [pages, changeConstructorRightBarSection]);

    const hStackClass = useStack({
        alignItems: 'center',
    });

    if (!pages.length) {
        return null;
    }

    return (
        <VStack>
            <div onClick={onClick} className={cn(styles.pageItem, hStackClass)}>
                <HStack className={styles.icon} alignItems="center">
                    {icon}
                </HStack>
                <Text role="main" size="xs">
                    {title}
                </Text>
            </div>
            <VStack className={styles.accordion}>
                {pages.map((page) => (
                    <PageItem key={`page-${page.id}`} page={page} onClose={onClose} />
                ))}
            </VStack>
        </VStack>
    );
};

export const ConstructorPagesMiniGrid = (props: ConstructorPagesMiniGridProps) => {
    const { pages, onClick: onClose } = props;

    const { aliasPagesWithTemplate, legalPages, accountPages, giftCardPages, loyaltyPages, userCreatedPages } =
        useSortConstructorPages(pages);

    return (
        <VStack>
            {aliasPagesWithTemplate.map((group) =>
                Array.isArray(group) ? (
                    <PageItemAccordion
                        key={`page-${group[0].alias}`}
                        pages={group.map((item, index) => {
                            if (isConstructorCartPageGroup(group)) {
                                return { ...item, title: getConstructorCartPageItemTitle(item) };
                            }

                            if (index === 0) {
                                return { ...item, title: 'Шаблон по умолчанию' };
                            }

                            return item;
                        })}
                        title={group[0].title}
                        icon={getConstructorPageIcon(group[0].alias)}
                        onClose={onClose}
                    />
                ) : (
                    <PageItem key={`page-${group.id}`} page={group} onClose={onClose} />
                )
            )}
            <PageItemAccordion
                pages={accountPages}
                title="Личный кабинет покупателя"
                icon={<IconUser color="gray" />}
                onClose={onClose}
            />
            <PageItemAccordion
                pages={legalPages}
                title="Юридические страницы"
                icon={<IconJob color="gray" />}
                onClose={onClose}
            />
            <PageItemAccordion
                pages={giftCardPages}
                title="Подарочная карта"
                icon={<IconGift color="gray" />}
                onClose={onClose}
            />
            <PageItemAccordion
                pages={loyaltyPages}
                title="Программа лояльности"
                icon={<IconCashback color="gray" />}
                onClose={onClose}
            />
            <PageItemAccordion
                pages={userCreatedPages}
                title="Дополнительные страницы"
                icon={<IconDocument color="gray" />}
                onClose={onClose}
            />
        </VStack>
    );
};
